import {
	Box,
	Button,
	Container,
	Heading,
	Img,
	Link,
	Stack,
	Text,
} from "@chakra-ui/react";
import { Link as ReactLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface IProps {
	headingTitle: string;
	headingDescription: string;
	headingDescription2?: string;
	leftButtonLink?: string;
	leftButtonText?: string;
	rightButtonLink?: string;
	rightButtonText?: string;
	ctaImageUrl?: string;
	ctaImageAlt?: string;
}

export const CallToAction = ({
	headingTitle,
	headingDescription,
	headingDescription2,
	leftButtonLink,
	leftButtonText,
	rightButtonLink,
	rightButtonText,
	ctaImageUrl,
	ctaImageAlt,
}: IProps): JSX.Element => {
	const { t } = useTranslation();

	return (
		<Box position="relative" overflow="hidden">
			<Container maxW="1480px" py={{ base: "12", md: "16", lg: "20" }} height="full">
				<Stack
					direction={{ base: "column", lg: "row" }}
					spacing={{ base: "10", lg: "12" }}
					align="center"
					minH={{ lg: "420px" }}
				>
					<Stack
						spacing={{ base: "8", md: "10" }}
						flex={{ lg: "0 0 46%" }}
						align={{ base: "center", lg: "flex-start" }}
						textAlign={{ base: "center", lg: "left" }}
					>
						<Stack spacing="4">
							<Stack
								spacing={{ base: "4", md: "6" }}
								maxW={{ base: "100%", lg: "2xl" }}
							>
								<Heading
									fontSize={{ base: "3xl", md: "5xl", xl: "6xl" }}
									lineHeight={{ base: "1.05", md: "1" }}
								>
									{headingTitle}
								</Heading>
								<Text fontSize={{ base: "lg", md: "xl" }} color="fg.muted">
									{headingDescription}
								</Text>
								{
									headingDescription2 && 
									<Text fontSize={{ base: "lg", md: "xl" }} color="fg.muted">
										{headingDescription2}
									</Text>
								}
							</Stack>
						</Stack>

						{(leftButtonText || rightButtonText) && (
							<Stack
								direction={{ base: "column", sm: "row" }}
								spacing="3"
								w={{ base: "100%", sm: "auto" }}
							>
								{leftButtonText && (
									<Link as={ReactLink} to={leftButtonLink || "/contact-us"} w={{ base: "100%", sm: "220px" }}>
										<Button w="full" variant="primary" size={{ base: "lg", md: "xl" }}>
											{leftButtonText}
										</Button>
									</Link>
								)}
								{rightButtonText && (
									<Link as={ReactLink} to={rightButtonLink || "/about-us"} w={{ base: "100%", sm: "220px" }}>
										<Button w="full" variant="secondary" size={{ base: "lg", md: "xl" }}>
											{rightButtonText}
										</Button>
									</Link>
								)}
							</Stack>
						)}
					</Stack>

					<Box
						flex={{ lg: "0 0 50%" }}
						w="full"
						height={{ base: "320px", md: "420px", lg: "420px" }}
						mx="auto"
						sx={{
							clipPath: { lg: "polygon(7% 0%, 100% 0%, 100% 100%, 0% 100%)" },
						}}
					>
						<Img
							boxSize="full"
							objectFit="cover"
							src={ctaImageUrl?.startsWith("/") ? ctaImageUrl : `/${ctaImageUrl || "get-started-cta.png"}`}
							alt={ctaImageAlt || t("landing.getStartedLink") || "Get started"}
						/>
					</Box>
				</Stack>
			</Container>
		</Box>
	);
};
